import { createCipheriv, createDecipheriv, createHash, createHmac, randomBytes, randomInt } from "node:crypto";

/**
 * X1 AUTH secret handling: opaque session and link tokens, one-time recovery
 * codes, and the AES-256-GCM seal for a TOTP secret at rest. Only the digest of
 * a token or a recovery code is ever stored; only the sealed bytes of a TOTP
 * secret are. Nothing here reads the environment: the key arrives as a string
 * the caller read through `envValue`, and its NAME (ACKRA_AUTH_ENC_KEY) is the
 * only thing an error message carries (CLAUDE.md section 10: credential names
 * only, values never).
 */

export const AUTH_ENC_KEY_ENV = "ACKRA_AUTH_ENC_KEY";

/** 32 random bytes, base64url: 256 bits, unguessable, safe in a URL and a cookie. */
export function newOpaqueToken(): string {
  return randomBytes(32).toString("base64url");
}

/** SHA-256 hex. A high-entropy token needs no salt or slow hash; the database keeps only this. */
export function hashOpaqueToken(token: string): string {
  return createHash("sha256").update(token, "utf8").digest("hex");
}

/** No 0, 1, i, l, o: the characters a person misreads when typing a code from paper. */
const RECOVERY_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

function recoveryCode(): string {
  let out = "";
  for (let i = 0; i < 10; i++) out += RECOVERY_ALPHABET[randomInt(RECOVERY_ALPHABET.length)];
  return `${out.slice(0, 5)}-${out.slice(5)}`;
}

/** `count` distinct one-time codes, shown once at enrolment and never stored in the clear. */
export function generateRecoveryCodes(count: number): string[] {
  const codes = new Set<string>();
  while (codes.size < count) codes.add(recoveryCode());
  return [...codes];
}

/** Case, spaces and the hyphen are not part of the code. */
export function normalizeRecoveryCode(typed: string): string {
  return typed.toLowerCase().replace(/[\s-]/g, "");
}

/**
 * HMAC-SHA-256 under the server key (fix round 1, item 8). A code carries about 2^49.5 bits, so a bare
 * SHA-256 of it is brute-forceable offline from a stolen table; keyed, the table alone proves nothing.
 */
export function hashRecoveryCode(typed: string, key: Buffer): string {
  return createHmac("sha256", key).update(normalizeRecoveryCode(typed), "utf8").digest("hex");
}

/** A 32-byte key from 64 hex characters or the base64 of 32 bytes. Throws by NAME, never echoing the value. */
export function parseAuthEncKey(raw: string): Buffer {
  const hex = /^[0-9a-fA-F]{64}$/.test(raw) ? Buffer.from(raw, "hex") : null;
  const b64 = /^[A-Za-z0-9+/]{43}=$/.test(raw) ? Buffer.from(raw, "base64") : null;
  const key = hex ?? b64;
  if (key === null || key.length !== 32) {
    throw new Error(`${AUTH_ENC_KEY_ENV} is unset or malformed: it must be 32 bytes, as 64 hex characters or 44 base64 characters.`);
  }
  return key;
}

const IV_BYTES = 12;
const TAG_BYTES = 16;

/** iv (12) | tag (16) | ciphertext. A fresh IV every time. */
export function sealSecret(plain: string, key: Buffer): Buffer {
  const iv = randomBytes(IV_BYTES);
  const cipher = createCipheriv("aes-256-gcm", key, iv);
  const body = Buffer.concat([cipher.update(plain, "utf8"), cipher.final()]);
  return Buffer.concat([iv, cipher.getAuthTag(), body]);
}

/** Throws on any tampering or the wrong key: GCM authenticates. */
export function openSecret(sealed: Buffer, key: Buffer): string {
  if (sealed.length < IV_BYTES + TAG_BYTES + 1) throw new Error("sealed secret is too short to be one");
  const decipher = createDecipheriv("aes-256-gcm", key, sealed.subarray(0, IV_BYTES));
  decipher.setAuthTag(sealed.subarray(IV_BYTES, IV_BYTES + TAG_BYTES));
  return Buffer.concat([decipher.update(sealed.subarray(IV_BYTES + TAG_BYTES)), decipher.final()]).toString("utf8");
}
